import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  MessageSquare, Send, Loader2, Bot, User, Sparkles,
  FileText, BarChart3, ClipboardList, Brain, X, Maximize2,
  Minimize2, History, Trash2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AI_MODES = [
  { 
    id: 'assistant', 
    name: 'AI Assistant', 
    icon: Brain,
    description: 'Protocol questions, compliance, documentation',
    color: 'bg-blue-500'
  },
  { 
    id: 'form_helper', 
    name: 'Form Helper', 
    icon: ClipboardList,
    description: 'Help completing clinical forms',
    color: 'bg-green-500'
  },
  { 
    id: 'report_generator', 
    name: 'Report Generator', 
    icon: FileText,
    description: 'Generate study reports and summaries',
    color: 'bg-purple-500'
  },
  { 
    id: 'data_analyst', 
    name: 'Data Analyst', 
    icon: BarChart3,
    description: 'Analyze clinical data and metrics',
    color: 'bg-orange-500'
  }
];

const AIAssistantPanel = ({ isOpen, onClose, context = {} }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('assistant');
  const [sessionId, setSessionId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/ai/conversations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const loadConversation = async (convSessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/ai/conversations/${convSessionId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.conversation.messages.map(m => ({
          role: m.role,
          content: m.content
        })));
        setSessionId(convSessionId);
        setMode(data.conversation.mode);
        setShowHistory(false);
        toast.success('Conversation loaded');
      }
    } catch (err) {
      toast.error('Failed to load conversation');
    }
  };

  const deleteConversation = async (convSessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/ai/conversations/${convSessionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setConversations(conversations.filter(c => c.session_id !== convSessionId));
        if (sessionId === convSessionId) {
          setMessages([]);
          setSessionId(null);
        }
        toast.success('Conversation deleted');
      }
    } catch (err) {
      toast.error('Failed to delete conversation');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    // Add placeholder for assistant response
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      const response = await fetch(`${API_URL}/api/registry/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          mode,
          session_id: sessionId,
          context: Object.keys(context).length > 0 ? context : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Request failed');
      }

      const data = await response.json();
      setSessionId(data.session_id);
      
      // Update the assistant message with full response
      setMessages(prev => {
        const newMessages = [...prev];
        const lastIdx = newMessages.length - 1;
        if (newMessages[lastIdx]?.role === 'assistant') {
          newMessages[lastIdx] = { 
            role: 'assistant', 
            content: data.response,
            streaming: false 
          };
        }
        return newMessages;
      });

    } catch (err) {
      console.error('AI error:', err);
      toast.error(err.message || 'Failed to get AI response');
      // Remove the empty assistant message
      setMessages(prev => prev.filter((m, i) => !(i === prev.length - 1 && m.role === 'assistant' && !m.content)));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    toast.success('Started new conversation');
  };

  const currentMode = AI_MODES.find(m => m.id === mode);
  const ModeIcon = currentMode?.icon || Brain;

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed z-50 transition-all duration-300 ${
        isExpanded 
          ? 'inset-4' 
          : 'bottom-4 right-4 w-[420px] h-[600px]'
      }`}
      data-testid="ai-assistant-panel"
    >
      <Card className="h-full flex flex-col shadow-2xl border-0 bg-white">
        {/* Header */}
        <CardHeader className="p-3 border-b bg-gradient-to-r from-[#1877F2] to-[#0d5bbd] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 ${currentMode?.color} rounded-lg flex items-center justify-center`}>
                <ModeIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">{currentMode?.name}</CardTitle>
                <p className="text-[10px] text-blue-100">Claude Opus 4</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-white hover:bg-white/20"
                onClick={() => { fetchConversations(); setShowHistory(!showHistory); }}
                data-testid="ai-history-button"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-white hover:bg-white/20"
                onClick={startNewConversation}
                data-testid="ai-new-conversation-button"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-white hover:bg-white/20"
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid="ai-expand-button"
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-white hover:bg-white/20"
                onClick={onClose}
                data-testid="ai-close-button"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Mode Selector - 2x2 Grid for better fit */}
        <div className="p-2 border-b bg-gray-50">
          <div className="grid grid-cols-2 gap-1">
            {AI_MODES.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    mode === m.id 
                      ? `${m.color} text-white shadow-sm` 
                      : 'bg-white text-gray-600 hover:bg-gray-100 border'
                  }`}
                  data-testid={`ai-mode-${m.id}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{m.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* History Panel - positioned below the 2x2 mode grid */}
        {showHistory && (
          <div className="absolute top-[155px] left-2 right-2 bg-white border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
            <div className="p-2 border-b bg-gray-50">
              <p className="text-xs font-medium text-gray-600">Recent Conversations</p>
            </div>
            {conversations.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">No conversation history</p>
            ) : (
              conversations.map((conv) => (
                <div 
                  key={conv.session_id}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 border-b last:border-0"
                >
                  <button
                    onClick={() => loadConversation(conv.session_id)}
                    className="flex-1 text-left"
                    data-testid={`ai-history-item-${conv.session_id}`}
                  >
                    <p className="text-xs font-medium text-gray-700 capitalize">{conv.mode.replace('_', ' ')}</p>
                    <p className="text-[10px] text-gray-400">{new Date(conv.updated_at).toLocaleString()}</p>
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                    onClick={() => deleteConversation(conv.session_id)}
                    data-testid={`ai-history-delete-${conv.session_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Messages */}
        <CardContent className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <Sparkles className="w-10 h-10 mb-3 text-[#1877F2]" />
              <p className="text-sm font-medium text-gray-600">How can I help you today?</p>
              <p className="text-xs mt-1">{currentMode?.description}</p>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div
              key={msg.timestamp || `msg-${idx}`}
              className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${currentMode?.color}`}>
                  <Bot className="w-4 h-4 text-white" />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-[#1877F2] text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {msg.streaming && (
                  <span className="inline-block w-2 h-4 bg-gray-400 animate-pulse ml-1" />
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input */}
        <div className="p-3 border-t bg-gray-50">
          <div className="flex gap-2">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Ask ${currentMode?.name}...`}
              className="flex-1 min-h-[40px] max-h-[100px] resize-none text-sm"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-[#1877F2] hover:bg-[#166FE5] h-10 w-10 p-0"
              data-testid="ai-send-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5 text-center">
            AI responses may be inaccurate. Always verify critical information.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AIAssistantPanel;
